import styled from '@emotion/styled';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { STATUS } from '@hotjar/comments/hooks/useCommentsApi';
import type { Thread } from '@hotjar/comments/domains/model';
import { getAll } from '@hotjar/comments/domains/api/highlights';
import { tokens, Card as DSCard } from '@hotjar/hotsauce';

import type { HighlightsListItem } from '@hotjar/webapp/domains/highlights/model';

import type { HeatmapImageRef } from '../Heatmaps/HeatmapImage';
import { HighlightPreview } from './HighlightPreview';
import { HIGHLIGHT_CARD_MAX_HEIGHT } from './HeatmapHighlightPreview';
import {
  HighlightCommentPreview,
  HIGHLIGHT_COMMENTS_PREVIEW_HEIGHT,
} from './HighlightCommentsPreview';
import { HighlightLabelsPreview } from './HighlightLabelsPreview';
import { getAutoCreatedComment } from '../getAutoCreatedComment';
import { HighlightCardActionsDropdown } from './HighlightCardActionsDropdown';

const ContainerCard = styled(DSCard)`
  height: ${HIGHLIGHT_CARD_MAX_HEIGHT}px;
  overflow: hidden;
  cursor: pointer;
  border-radius: ${tokens.radius.l};

  transition: box-shadow 0.2s ease-out;
  cursor: pointer;
  ${tokens.elevation(1)};
  &:hover {
    ${tokens.elevation(3)};
  }
  &:focus {
    box-shadow: 0px 0px 0px 2px #5b72cb;
  }
`;

const Tag = styled.div`
  border-radius: ${tokens.radius.m};
  background-color: ${tokens.ALIAS_TEXT_COLORS.text_success};
  height: 16px;
  color: ${tokens.color.white};
  position: absolute;
  top: 12px;
  left: 16px;
  padding: 0px ${tokens.spacer.x0};
  z-index: 1;
  display: flex;
  align-items: center;
  ${tokens.text('subtitle3')}
  text-transform: uppercase;
`;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const LabelsAndComments = styled.div<{ hasComments: boolean }>`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0px;
  transition: transform 0.2s ease-out;
  transform: translateY(
    ${({ hasComments }) => (hasComments ? 0 : HIGHLIGHT_COMMENTS_PREVIEW_HEIGHT)}px
  );
`;

interface CardProps {
  highlight: HighlightsListItem;
  setSelectedHighlightId: (id: string) => void;
  setDropdownHighlightId: (id: string) => void;
  setIsShortLinkWidgetShown: (isShown: boolean) => void;
  siteId: string;
  currentUserId: number | undefined;
  startRemoval: () => void;
}

export const Card = ({
  highlight,
  setSelectedHighlightId,
  setDropdownHighlightId,
  setIsShortLinkWidgetShown,
  siteId,
  currentUserId,
  startRemoval,
}: CardProps): JSX.Element => {
  const { t } = useTranslation();

  const svgRef = useRef<HeatmapImageRef>(null);

  const [isHovered, setIsHovered] = useState(false);

  const [, setStatus] = useState(STATUS.INITIAL);
  const [threads, setThreads] = useState<Array<Thread>>([]);
  const isAutoCreated = highlight?.autoCreated ?? false;

  const fetchData = useCallback(async () => {
    try {
      setStatus(STATUS.LOADING);

      if (siteId === null) {
        return;
      }

      const commentsResult = await getAll({
        siteId,
        widgetId: Number(highlight?.id),
        widgetType: 'highlight',
        scopeId:
          highlight.commentId && Number(highlight.commentId) ? Number(highlight.commentId) : null,
        token: null,
      });

      if (isAutoCreated && commentsResult.length === 0) {
        const autoCreatedComment = getAutoCreatedComment(t, highlight.createdDate);

        setThreads([{ ...autoCreatedComment, metadata: {}, widgetId: 0, replies: [] }]);
      } else {
        setThreads(commentsResult);
      }
    } catch (e) {
      setStatus(STATUS.ERROR);
    } finally {
      setStatus(STATUS.SUCCESS);
    }
  }, [t, siteId, isAutoCreated, highlight?.id, highlight.commentId, highlight.createdDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
    >
      <ContainerCard pointer-events="all">
        {highlight.solved && <Tag>{t('highlights:single.resolved')}</Tag>}
        <HighlightCardActionsDropdown
          highlight={highlight}
          setDropdownHighlightId={setDropdownHighlightId}
          setIsShortLinkWidgetShown={setIsShortLinkWidgetShown}
          currentUserId={currentUserId}
          startRemoval={startRemoval}
          isHovered={isHovered}
          siteId={siteId}
        />
        <Wrapper onClick={() => setSelectedHighlightId(highlight.id)}>
          <HighlightPreview
            highlight={highlight}
            svgRef={svgRef}
            hasComments={threads.length > 0}
            siteId={siteId}
          />
          <LabelsAndComments hasComments={threads.length > 0}>
            <HighlightLabelsPreview highlight={highlight} />
            <HighlightCommentPreview currentUserId={currentUserId} threads={threads} />
          </LabelsAndComments>
        </Wrapper>
      </ContainerCard>
    </div>
  );
};
